import { useMemo, useCallback } from 'react';
import {
  SpaceBetween,
  Button,
  Toggle,
  Input,
  Container,
  Header,
  Alert,
} from '@cloudscape-design/components';
import { TimezoneSelect } from './TimezoneSelect';

/** Validates a time string matches HH:MM format (0:00-23:59) */
const TIME_FORMAT_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/**
 * Validates business hours schedule values for time format errors.
 * Returns an array of error messages (empty = valid).
 * Can be called externally before save to prevent persisting invalid times.
 */
export function validateScheduleTimes(values: Record<string, unknown>): string[] {
  const errors: string[] = [];
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  for (const day of days) {
    const enabled = values[`${day}_enabled`] === true || values[`${day}_enabled`] === 'true';
    if (!enabled) continue;
    const start = values[`${day}_startTime`] as string;
    const end = values[`${day}_endTime`] as string;
    if (start && !TIME_FORMAT_PATTERN.test(start)) {
      errors.push(`${day.charAt(0).toUpperCase() + day.slice(1)} Start Time: invalid format "${start}". Use HH:MM (e.g. 08:00).`);
    }
    if (end && !TIME_FORMAT_PATTERN.test(end)) {
      errors.push(`${day.charAt(0).toUpperCase() + day.slice(1)} End Time: invalid format "${end}". Use HH:MM (e.g. 17:00).`);
    }
  }
  return errors;
}

interface WeeklyScheduleGridProps {
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  errors?: Record<string, string>;
}

interface DaySchedule {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const DAYS: DayName[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<DayName, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

/**
 * Compact row-based weekly schedule grid for business-hours configurations.
 * Days as rows, columns: Enabled (checkbox), Start Time, End Time.
 * Includes "Copy to All Weekdays" and "Copy to All Days" buttons.
 *
 * Requirements: 6.1–6.12
 */
export function WeeklyScheduleGrid({ values, onChange, errors }: WeeklyScheduleGridProps) {
  const timezone = (values.timeZone as string) ?? '';

  // Deserialize flat payload to WeekSchedule
  const schedule = useMemo((): Record<DayName, DaySchedule> => {
    const result = {} as Record<DayName, DaySchedule>;
    for (const day of DAYS) {
      result[day] = {
        enabled: values[`${day}_enabled`] === true || values[`${day}_enabled`] === 'true',
        startTime: (values[`${day}_startTime`] as string) ?? '',
        endTime: (values[`${day}_endTime`] as string) ?? '',
      };
    }
    return result;
  }, [values]);

  // Serialize WeekSchedule back to flat payload
  const updateSchedule = useCallback((newSchedule: Record<DayName, DaySchedule>, newTimezone?: string) => {
    const payload: Record<string, unknown> = {
      timeZone: newTimezone ?? timezone,
      businessHoursType: 'default',
    };
    for (const day of DAYS) {
      const ds = newSchedule[day];
      payload[`${day}_enabled`] = ds.enabled;
      payload[`${day}_startTime`] = ds.enabled ? ds.startTime : '';
      payload[`${day}_endTime`] = ds.enabled ? ds.endTime : '';
    }
    onChange(payload);
  }, [onChange, timezone]);

  const updateDay = (day: DayName, field: keyof DaySchedule, value: unknown) => {
    const newSchedule = { ...schedule, [day]: { ...schedule[day], [field]: value } };
    if (field === 'enabled' && !value) {
      newSchedule[day].startTime = '';
      newSchedule[day].endTime = '';
    }
    updateSchedule(newSchedule);
  };

  const copyToWeekdays = () => {
    const monday = schedule.monday;
    const newSchedule = { ...schedule };
    for (const day of ['tuesday', 'wednesday', 'thursday', 'friday'] as DayName[]) {
      newSchedule[day] = { ...monday };
    }
    updateSchedule(newSchedule);
  };

  const copyToAllDays = () => {
    const monday = schedule.monday;
    const newSchedule = { ...schedule };
    for (const day of DAYS) {
      newSchedule[day] = { ...monday };
    }
    updateSchedule(newSchedule);
  };

  // Validation: time format and start >= end for enabled days
  const isValidTime = (t: string): boolean => TIME_FORMAT_PATTERN.test(t);

  const getRowError = (day: DayName): string | undefined => {
    const ds = schedule[day];
    if (!ds.enabled) return undefined;

    // Check format first
    if (ds.startTime && !isValidTime(ds.startTime)) return 'Invalid time format. Please enter time in HH:MM format.';
    if (ds.endTime && !isValidTime(ds.endTime)) return 'Invalid time format. Please enter time in HH:MM format.';

    if (!ds.startTime || !ds.endTime) return undefined;

    // Normalize times to minutes for reliable comparison
    const toMinutes = (t: string): number => {
      const [h, m] = t.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    if (toMinutes(ds.startTime) >= toMinutes(ds.endTime)) return 'End time must be after start time';
    return undefined;
  };

  return (
    <Container header={<Header variant="h3">Weekly Schedule</Header>}>
      <SpaceBetween size="m">
        <TimezoneSelect
          value={timezone}
          onChange={(tz) => updateSchedule(schedule, tz)}
          error={errors?.timeZone}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '120px 60px 120px 120px', gap: '8px', alignItems: 'center' }}>
          <strong>Day</strong>
          <strong>Enabled</strong>
          <strong>Start Time</strong>
          <strong>End Time</strong>

          {DAYS.map((day) => {
            const ds = schedule[day];
            const rowError = getRowError(day);
            return (
              <>
                <div key={`${day}-label`}>{DAY_LABELS[day]}</div>
                <div key={`${day}-toggle`}>
                  <Toggle
                    checked={ds.enabled}
                    onChange={({ detail }) => updateDay(day, 'enabled', detail.checked)}
                  />
                </div>
                <div key={`${day}-start`}>
                  <Input
                    value={ds.startTime}
                    onChange={({ detail }) => updateDay(day, 'startTime', detail.value)}
                    placeholder="HH:MM"
                    disabled={!ds.enabled}
                  />
                </div>
                <div key={`${day}-end`}>
                  <Input
                    value={ds.endTime}
                    onChange={({ detail }) => updateDay(day, 'endTime', detail.value)}
                    placeholder="HH:MM"
                    disabled={!ds.enabled}
                  />
                  {rowError && <Alert type="error">{rowError}</Alert>}
                </div>
              </>
            );
          })}
        </div>

        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={copyToWeekdays}>Copy to All Weekdays</Button>
          <Button onClick={copyToAllDays}>Copy to All Days</Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
}
